// timelapseReplay — deterministic replay of a merged stroke log into a
// timelapse frame stream (絵ログ T1 — growth-timelapse-share-2026-07-02.md
// §5.1 / §5.2, consult 2026-07-02 E ログ方式).
//
// The driver builds a **completely separate** offscreen `EngineCore`
// (shared drawing-core = the same bit-perfect rasterizer the live room
// engine and the server snapshot worker run) at the room's canvas size,
// white-bg blank, and applies the merged event list in seq order. It
// never touches the live canvas, the WS transport or the live
// `CanvasDrawingEngine` — the invariant of this slice is "新規モジュール
// のみ、ライブ描画経路不変".
//
// Event application mirrors the server's `SnapshotEngine.applyEvent`
// switch (packages/server/.../SnapshotEngine.ts) minus the Wasm bridge
// and PNG plumbing — same `nowMs: ev.ts` threading, same mask default,
// same penParams/aa passthrough — so a replayed log lands on the same
// bytes the room's snapshots were built from.
//
// Frame extraction: `compositeAllForExport()` = every layer over the
// canvas background, in-flight deferred-commit stroke buffers folded in
// (a stroke whose `end` sits after the current cutoff — or was trimmed
// off — still shows, matching what viewers saw live). Name labels / chat
// are DOM overlays and never enter the engine — キャンバス合成のみ (§5.1,
// watermark 等の overlay は T2 の責務でこの層には入れない).
//
// Threading: this module is **worker-compatible but main-thread-hosted
// for now** — it touches no DOM (OffscreenCanvas exists in workers, and
// the scaler degrades to pure TS without it). Replay throughput measured
// at ~vastly-faster-than-realtime (see T1 report / docs) and the driver
// yields to the event loop between event chunks, so the MVP runs it on
// the main thread; if T2's encoder wants full isolation the whole
// generator moves into a worker without code changes here.

import {
  applyRegionMove,
  EngineCore,
  type RegionImage,
  UndoBaselineStore,
} from '@oekaki/drawing-core';
import {
  CLASSIC_LAYER_COUNT,
  type StrokeLogEvent,
  type WhiteboardImagepaste,
  type WhiteboardRegion,
} from '@oekaki/protocol';
import type { Logger } from '../../application/ports/Logger.js';
import { type BackgroundColor, backgroundAt, DEFAULT_BACKGROUND } from './backgroundAt.js';
import { type CropRect, cropRgba } from './cropBox.js';
import {
  DEFAULT_TIMELAPSE_FPS,
  type FramePlan,
  planFrames,
  type TimelapseMode,
} from './framePlan.js';
import {
  computeOutputSize,
  createFrameScaler,
  type FrameRgba,
  type FrameScaler,
  type FrameSize,
} from './frameScale.js';
import { collectVisibleRestoreRebuilds, computeLineageSeedPlan } from './lineageSeed.js';
import {
  assertSeqRangeList,
  type SeqRange,
  trimEventsToRange,
  trimEventsToRanges,
} from './segmentMerge.js';

/** Default output cap — 720p 級 (§5.1). Long edge in px. */
export const DEFAULT_MAX_EDGE = 1280;

/**
 * How many events the driver applies before voluntarily yielding to the
 * event loop (main-thread friendliness). One macrotask hop every N events
 * keeps input latency acceptable while adding negligible overhead
 * relative to the rasterize cost.
 */
const EVENTS_PER_BREATH = 4_096;

/** One output frame. `rgba` is straight-α, `width × height` from `output`. */
export interface TimelapseFrame {
  /** 0-based frame index. */
  readonly index: number;
  /** Presentation timestamp (µs) = index / fps — ready for WebCodecs `VideoFrame`. */
  readonly timestampUs: number;
  /** 1 / fps in µs. */
  readonly durationUs: number;
  readonly width: number;
  readonly height: number;
  readonly rgba: FrameRgba;
}

/** Generator return value — summary the caller can read after exhaustion. */
export interface TimelapseReplaySummary {
  /**
   * Events applied to the engine within the **visible** (trimmed) range —
   * the frame-driving count. The silent 土台 seed events (the pre-start base,
   * §5) are NOT counted here: they are applied before frame 0 with no frames
   * captured, and in the default (start = 最後の全消しの次) case the seed is a
   * single wipe on a blank engine = a no-op, so this stays byte-identical.
   * 系譜再構築 (whiteboard.restore マーカーでの復元元範囲の無音再適用、§4.5-2)
   * のイベントも同様に数えない (マーカー自体は可視イベントとして 1 数える)。
   */
  readonly appliedEvents: number;
  /**
   * `whiteboard.imagepaste` events encountered (seed + visible, same scope
   * as {@link skippedImagepastes}). Lets T3 phrase the warning with a count
   * ("3 件中 1 件の貼り付け画像を再生できませんでした") instead of an
   * unqualified "貼り付け画像は再生されません".
   */
  readonly totalImagepastes: number;
  /**
   * `whiteboard.imagepaste` events that could NOT be painted: no
   * `resolveImagepaste` was supplied at all, OR it resolved `null` (re-sign
   * failed, fetch failed, decode failed — see `imagepasteResolve.ts`,
   * vandalism-recovery-ui-2026-09-27.md「タイムラプスの imagepaste 再生と
   * して先行実装する」節). A skipped paste still advances `appliedEvents`
   * (the event is consumed, replay continues) but paints nothing — same
   * "skip + warn" rule as `skippedSegments` / `gaps`.
   */
  readonly skippedImagepastes: number;
}

/**
 * Resolve a `whiteboard.imagepaste` event to decoded pixels, or `null` if it
 * can't be replayed (re-sign / fetch / decode failure — the caller counts it
 * via `skippedImagepastes` and moves on). The returned `width`/`height` are
 * the DECODED (natural) pixel dimensions of the fetched image — NEVER the
 * broadcast's `width`/`height` fields — mirroring `EngineCore.applyImagepaste`
 * (which paints at the RGBA buffer's own dimensions) and the live path
 * (`CanvasDrawingEngine.fetchAndApplyImagepaste`). Omit this option entirely
 * to keep the pre-existing "always skip" MVP behaviour (e.g. tests that don't
 * care about imagepaste). Production wiring: `imagepasteResolve.ts`'s
 * `createImagepasteResolver(...).resolve`.
 */
export type ResolveImagepasteFn = ((ev: WhiteboardImagepaste) => Promise<{
  readonly rgba: Uint8ClampedArray;
  readonly width: number;
  readonly height: number;
} | null>) & {
  /**
   * `whiteboard.region` (範囲の移動・コピー) の画像解決。items と同じ順の decode 済み画像、
   * 1 枚でも取れなければ null。未指定なら region は「スキップ」と数える。
   * `imagepasteResolve.ts` の resolver は resolve に付けて返す (受け渡しを増やさないため)。
   */
  readonly region?: (ev: WhiteboardRegion) => Promise<RegionImage[] | null>;
};

export interface CreateTimelapseFrameSourceOptions {
  /** Merged (seq-ordered, overlap-resolved) event list from `strokeLogTimeline`. */
  readonly events: readonly StrokeLogEvent[];
  /** Room canvas size (`roomMeta.settings.canvasWidth/Height`). */
  readonly canvas: FrameSize;
  /**
   * §5.2 トリム — 可視フェーズの**順序付き離散範囲リスト** (inclusive、
   * §4.7 系譜連結ドメインの一般化)。不変条件 = 非空・昇順・互いに素
   * (`assertSeqRangeList`、違反は TypeError = 呼び出し側バグ)。単一範囲は
   * 長さ 1 のリスト。Omit = full range。
   *
   * 意味論: 範囲を順に連結した仮想タイムラインとして可視再生する。範囲間の
   * ジャンプでは**リセットも seed 再構築もしない** — 範囲外のイベントを適用
   * しないだけ (系譜チェーンでは「範囲 k 再生終了状態 = 範囲 k+1 の文書の
   * 開始状態 (復元種) と bit-perfect 一致」が成立するため。`lineageSeed.ts`
   * ヘッダの「チェーンは重ねて適用する」意味論)。restore マーカー・全消し・
   * 中間文書のイベントは範囲外なのでフレームに現れない。範囲リストが 1 本の
   * 連続範囲なら従来の単一 `seqRange` とバイト一致。
   */
  readonly seqRanges?: readonly SeqRange[];
  /** §5.2 長さ/倍速. Default: `{ kind: 'duration', seconds: 30 }`. */
  readonly mode?: TimelapseMode;
  /** Default {@link DEFAULT_TIMELAPSE_FPS}. */
  readonly fps?: number;
  /** Output long-edge cap. Default {@link DEFAULT_MAX_EDGE} (720p 級). */
  readonly maxEdge?: number;
  /**
   * §5.5 動画長 cap (秒) — resolved from the per-preset / per-platform memory
   * budget (`resolveTimelapseQuality`). Drives `planFrames.maxFrames`
   * (= `ceil(maxSeconds × fps)`) for BOTH modes: duration mode's
   * `seconds × fps` is clamped by it, and multiplier mode stretches its
   * step to fit. Omit = `planFrames`'s own `HARD_MAX_OUTPUT_SECONDS` guard
   * (dev harness / tests).
   */
  readonly maxSeconds?: number;
  /**
   * §5.2 自動クロップ (T2) — crop every composited frame to this canvas
   * subrect BEFORE downscaling, so the output resolution budget is spent
   * on the changed region. Must lie fully inside `canvas` with positive
   * integer dims (the `computeChangedRegionBox` helper always satisfies
   * this — a violation is a caller bug ⇒ `TypeError` at setup). Omit or
   * pass the full-canvas rect for the uncropped behaviour.
   */
  readonly sourceRect?: CropRect;
  /**
   * Layer count for the offscreen engine. Default: derived from the
   * events (max layerId + 1, floor {@link CLASSIC_LAYER_COUNT}) so a log
   * recorded from any current layout replays onto enough layers.
   */
  readonly layerCount?: number;
  /**
   * Initial canvas background override. Default (omitted): derived from
   * the FULL `events` log via `backgroundAt` (Bug B/C, fix/timelapse-
   * background) — the color in effect immediately before the first
   * applied event (seed or visible), correctly inheriting across
   * `whiteboard.clear` (does not change it) and `whiteboard.restore`
   * (switches to the color at `restoredFromSeq`). Falls back to opaque
   * white when the log has no earlier `background-set` to find.
   * `whiteboard.background-set` events in the log override it mid-replay
   * exactly like live, same as before.
   */
  readonly background?: { readonly rgb: number; readonly alpha: number };
  /** Scaler override (tests inject `boxDownscaleRgba`). Default: best available. */
  readonly scaler?: FrameScaler;
  /**
   * `whiteboard.imagepaste` pixel resolver (vandalism-recovery-ui-2026-09-27.md
   * 「タイムラプスの imagepaste 再生」節). Omit to keep every paste a no-op
   * (pre-existing MVP behaviour — `skippedImagepastes` still counts them).
   * Production callers (`encodeJob.ts` / `previewFrame.ts`) both wire the
   * SAME `imagepasteResolve.ts` resolver so the finished video and the edit
   * screen's range preview never diverge.
   */
  readonly resolveImagepaste?: ResolveImagepasteFn;
  readonly logger?: Logger;
}

export interface TimelapseFrameSource {
  /** Frame timing plan — frameCount / outputDurationSeconds for the UI + T2 muxer. */
  readonly plan: FramePlan;
  /** Output frame dimensions (even, ≤ maxEdge, never upscaled). */
  readonly output: FrameSize;
  /** Trimmed event count the replay will apply. */
  readonly totalEvents: number;
  /**
   * Start the replay. Frames are produced lazily — the T2 encoder pulls
   * one, encodes it, then pulls the next, so at most one original-size
   * composite + one scaled frame are alive at a time (§5.1 フレームを
   * 溜め込まない). The generator's return value is the replay summary.
   *
   * Each call runs an independent replay on a fresh engine (safe to call
   * again after a parameter change — e.g. the user moves the trim slider
   * and regenerates).
   */
  frames(): AsyncGenerator<TimelapseFrame, TimelapseReplaySummary>;
}

/**
 * Build a timelapse frame source from a merged event list. Pure setup —
 * no engine is allocated until `frames()` is iterated.
 */
export const createTimelapseFrameSource = (
  options: CreateTimelapseFrameSourceOptions,
): TimelapseFrameSource => {
  const fps = options.fps ?? DEFAULT_TIMELAPSE_FPS;
  const mode: TimelapseMode = options.mode ?? { kind: 'duration', seconds: 30 };
  const visibleRanges = options.seqRanges;
  if (visibleRanges !== undefined) {
    assertSeqRangeList(visibleRanges, 'createTimelapseFrameSource');
  }
  // 土台 seed (§5 A 案 + 系譜 seed 拡張 §4.5-2): 開始点より前の「現在の文書」の
  // 描画を、フレームを撮らずに無音で apply して土台を作る。現在の文書が
  // `whiteboard.restore` で始まる場合は、まず全レイヤ + 背景をリセットして系譜
  // チェーン (復元元ドキュメントの範囲、root→leaf) を無音再適用してから
  // マーカー以降の prelude を適用する。restore が絡まないログでは
  // computeSeedRange と同一の単一範囲 (開始点が最後の全消し直後 = 既定のときは
  // 白紙化 no-op) になり出力は従来と完全一致する。
  //
  // 離散範囲リスト (§4.7) では seed は**先頭範囲**にだけ関わる: 可視開始が
  // 先頭範囲の途中なら既存の系譜 seed + prelude で土台を作り、先頭範囲の先頭
  // (= 系譜 root の文書開始 = 全消し or ログ先頭) から始まるなら土台は不要
  // (範囲先頭の wipe は白紙エンジンに no-op / ログ先頭は seed 対象なし)。
  // 2 本目以降の範囲の手前に seed は要らない — 範囲 k までの再生結果がそのまま
  // 範囲 k+1 の土台 (復元種と bit-perfect 一致) だから。
  const seedPlan = computeLineageSeedPlan(options.events, visibleRanges?.[0]);
  const seedSteps: MaterializedSeedStep[] = [];
  // Bug A 修正 (fix/timelapse-background): `seedPlan.leadingRun` が付くのは
  // 先頭範囲がそれ自体、文書境界 (全消し自身 / ログ先頭) で始まるケース — その
  // 非描画イベント列は「無音の土台」に回すので、先頭範囲からは取り除く
  // (frame 0 = 描き始める直前の状態、§ module header)。既定は元の
  // `visibleRanges` そのまま (leadingRun が無ければ何も変わらない)。
  let effectiveRanges = visibleRanges;
  if (seedPlan !== null) {
    if (seedPlan.rebuild !== null) {
      seedSteps.push({
        resetBefore: true,
        // Bug B/C 修正: 系譜土台へのリセットは「復元元スナップショット時点の
        // 背景色」に切り替える (= 実際の restore と同じ意味論)。
        resetBackground: backgroundAt(options.events, seedPlan.rebuildRestoredFromSeq ?? 0),
        events: seedPlan.rebuild.ranges.flatMap((r) => trimEventsToRange(options.events, r)),
      });
    }
    if (seedPlan.prelude !== null) {
      seedSteps.push({
        resetBefore: false,
        events: trimEventsToRange(options.events, seedPlan.prelude),
      });
    }
    if (seedPlan.leadingRun !== null && visibleRanges !== undefined) {
      seedSteps.push({
        resetBefore: false,
        events: trimEventsToRange(options.events, seedPlan.leadingRun),
      });
      const first = visibleRanges[0];
      if (first !== undefined) {
        const rest = visibleRanges.slice(1);
        const trimmedFirst: SeqRange[] =
          seedPlan.leadingRun.toSeq < first.toSeq
            ? [{ fromSeq: seedPlan.leadingRun.toSeq + 1, toSeq: first.toSeq }]
            : [];
        effectiveRanges = [...trimmedFirst, ...rest];
      }
    }
  }
  // 可視範囲内の restore マーカー (スライダーが境界を跨ぐケース): マーカー時点で
  // リセット + 系譜範囲を無音再適用する材料を seq → イベント列で先に引いておく。
  // 系譜連結ドメインの範囲リストではマーカーは範囲間に落ちるため Map は空 =
  // 範囲ジャンプでリセットは起こらない (§4.7)。
  const rebuildEventsBySeq = new Map<number, readonly StrokeLogEvent[]>();
  for (const [seq, rebuild] of collectVisibleRestoreRebuilds(options.events, effectiveRanges)) {
    rebuildEventsBySeq.set(
      seq,
      rebuild.ranges.flatMap((r) => trimEventsToRange(options.events, r)),
    );
  }
  // Visible ranges — the concatenated list drives the frame plan (密度は可視
  // イベント数基準のまま。範囲間の実時間ギャップは duration モードでは存在
  // 自体が消え、multiplier モードでは既存の inter-event clamp (最大 2 秒) に
  // 収まる = 動画に死に時間を作らない、§4.7)。
  const events = trimEventsToRanges(options.events, effectiveRanges);
  const plan = planFrames({
    eventCount: events.length,
    mode,
    fps,
    ...(options.maxSeconds !== undefined
      ? { maxFrames: Math.max(1, Math.ceil(options.maxSeconds * fps)) }
      : {}),
    ...(mode.kind === 'multiplier' ? { eventTimestampsMs: events.map((ev) => ev.ts) } : {}),
  });
  const sourceRect = resolveSourceRect(options.sourceRect, options.canvas);
  const output = computeOutputSize(
    sourceRect.width,
    sourceRect.height,
    options.maxEdge ?? DEFAULT_MAX_EDGE,
  );

  return {
    plan,
    output,
    totalEvents: events.length,
    frames: () =>
      replayFrames(seedSteps, events, rebuildEventsBySeq, plan, sourceRect, output, options),
  };
};

/**
 * 無音 seed の 1 ステップ (系譜 seed 拡張 §4.5-2)。`resetBefore` はステップ
 * 適用前に全レイヤ + 背景を初期状態に戻す (= restore の「キャンバス全体が種に
 * 置き換わる」意味論)。restore の無い従来ケースはリセット無しの 1 ステップ。
 */
interface MaterializedSeedStep {
  readonly resetBefore: boolean;
  readonly events: readonly StrokeLogEvent[];
  /**
   * `resetBefore` が true のときのリセット先背景色 (Bug B/C 修正)。省略時は
   * {@link DEFAULT_BACKGROUND} にフォールバック (実際には常に渡される —
   * `createTimelapseFrameSource` が `backgroundAt` で計算する)。
   */
  readonly resetBackground?: BackgroundColor;
}

/**
 * Validate the crop rect against the canvas (T2 自動クロップ). Defaults to
 * the full canvas. Violations are caller bugs (`computeChangedRegionBox`
 * clamps), so this throws rather than degrading.
 */
const resolveSourceRect = (rect: CropRect | undefined, canvas: FrameSize): CropRect => {
  if (rect === undefined) return { x: 0, y: 0, width: canvas.width, height: canvas.height };
  if (
    !Number.isInteger(rect.x) ||
    !Number.isInteger(rect.y) ||
    !Number.isInteger(rect.width) ||
    !Number.isInteger(rect.height) ||
    rect.width <= 0 ||
    rect.height <= 0 ||
    rect.x < 0 ||
    rect.y < 0 ||
    rect.x + rect.width > canvas.width ||
    rect.y + rect.height > canvas.height
  ) {
    throw new TypeError(
      `createTimelapseFrameSource: sourceRect ${rect.x},${rect.y} ${rect.width}x${rect.height} outside canvas ${canvas.width}x${canvas.height}`,
    );
  }
  return rect;
};

// ─── Replay loop ────────────────────────────────────────────────────────────

async function* replayFrames(
  seedSteps: readonly MaterializedSeedStep[],
  events: readonly StrokeLogEvent[],
  rebuildEventsBySeq: ReadonlyMap<number, readonly StrokeLogEvent[]>,
  plan: FramePlan,
  sourceRect: CropRect,
  output: FrameSize,
  options: CreateTimelapseFrameSourceOptions,
): AsyncGenerator<TimelapseFrame, TimelapseReplaySummary> {
  const scaler = options.scaler ?? createFrameScaler();
  // Layer count must cover the silent bases (seed steps + mid-replay lineage
  // rebuilds) AND the visible range — a base stroke on a higher layer than
  // any visible stroke still needs its slot. In the default case the bases
  // are empty ⇒ identical to before.
  let layerCount = options.layerCount ?? deriveLayerCount(events);
  if (options.layerCount === undefined) {
    for (const step of seedSteps) {
      layerCount = Math.max(layerCount, deriveLayerCount(step.events));
    }
    for (const rebuildEvents of rebuildEventsBySeq.values()) {
      layerCount = Math.max(layerCount, deriveLayerCount(rebuildEvents));
    }
  }
  // Bug B/C 修正: seed 開始時点 (= 最初に適用するイベントの直前) の背景色を
  // ログ全体から求める (呼び出し側が `options.background` を渡していれば
  // それを優先 — 既存 API の後方互換)。全消しは背景色を変えないので、2 文書
  // 以上前の background-set / restore を跨いだ継承も `backgroundAt` が扱う。
  const firstAppliedEvent = seedSteps.find((s) => s.events.length > 0)?.events[0] ?? events[0];
  const initialBackground: BackgroundColor =
    options.background ??
    (firstAppliedEvent !== undefined
      ? backgroundAt(options.events, firstAppliedEvent.seq - 1)
      : DEFAULT_BACKGROUND);
  // Fresh, offscreen, backend-less engine — `attachBackend` is optional
  // (the server snapshot engine relies on the same property) and we only
  // read composites, never blit to a display surface.
  const core = new EngineCore({
    width: options.canvas.width,
    height: options.canvas.height,
    layerCount,
    background: initialBackground,
  });
  // conflict-aware Undo B' bookkeeping (mirrors the live client's — see
  // `applyEvent`'s `stroke.cancelled` case). Recreated (not just cleared) in
  // `resetForRestore` alongside `core`'s content wipe, so tracked strokes
  // from before a lineage reset never leak into a post-reset rewind.
  let undoStore = new UndoBaselineStore(options.canvas.width, options.canvas.height, layerCount);

  const durationUs = Math.round(1_000_000 / plan.fps);
  let applied = 0;
  let totalImagepastes = 0;
  let skippedImagepastes = 0;
  let sinceBreath = 0;
  const resolveImagepaste = options.resolveImagepaste;

  const breathe = async (): Promise<void> => {
    // Cooperative breathing (main-thread hosting, see module header).
    // A macrotask hop — `await Promise.resolve()` would NOT let paint /
    // input run, so a real timeout is used.
    sinceBreath++;
    if (sinceBreath >= EVENTS_PER_BREATH) {
      sinceBreath = 0;
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
    }
  };

  // restore の「キャンバス全体が種に置き換わる」意味論: 全レイヤ透明化
  // (StrokeRenderer.clearLayer は対象レイヤの in-flight stroke も落とす) +
  // 背景を「復元元スナップショット時点の背景色」へ (Bug B/C 修正 —
  // `backgroundAt(restoredFromSeq)`、呼び出し側が計算して渡す)。
  const resetForRestore = (bg: BackgroundColor): void => {
    core.clearLayer(undefined);
    core.setBackground(bg);
    // Fresh B' bookkeeping — a restore wipes the canvas content, so any
    // stroke tracked from before it is no longer replayable/relevant
    // (its pixels are gone; a stray cancel for it is just ignored, same as
    // the live client's "unknown sid" idempotent no-op).
    undoStore = new UndoBaselineStore(options.canvas.width, options.canvas.height, layerCount);
  };

  // 系譜土台 / seed の無音適用 (NO frames)。imagepaste は土台でも数える —
  // 「貼り付け画像は再生されません」の警告は土台にも等しく当てはまる。
  const applySilently = async (list: readonly StrokeLogEvent[]): Promise<void> => {
    for (const ev of list) {
      if (ev.t === 'whiteboard.imagepaste' || ev.t === 'whiteboard.region') totalImagepastes++;
      if (
        (await applyEvent(core, undoStore, ev, options.logger, resolveImagepaste)) ===
        'imagepaste-skipped'
      ) {
        skippedImagepastes++;
      }
      await breathe();
    }
  };

  // ── Seed phase: build the pre-start base silently (NO frames). ─────────────
  // Frame 0 (below) already shows this base folded in. In the default case
  // this loop is empty (seedSteps = []) so the visible phase is untouched.
  // 現在の文書が restore で始まる場合は resetBefore 付きの系譜ステップが先頭に
  // 入る (系譜 seed 拡張 §4.5-2 — 白紙エンジンへのリセットは no-op だが、
  // 可視ループ内の再構築と同じ経路に揃える)。
  for (const step of seedSteps) {
    if (step.resetBefore) resetForRestore(step.resetBackground ?? DEFAULT_BACKGROUND);
    await applySilently(step.events);
  }

  for (let k = 0; k < plan.frameCount; k++) {
    const cutoff = plan.eventCutoffs[k] ?? applied;
    while (applied < cutoff) {
      const ev = events[applied];
      applied++;
      if (ev === undefined) continue;
      if (ev.t === 'whiteboard.restore') {
        // 可視範囲がドキュメント境界を跨いだ (§4.5-2 意味論 3): キャンバスを
        // リセットし、系譜チェーン (root→leaf) を無音再適用してからフレーム化を
        // 続行する — フレームは撮らず一瞬で復元後の絵に切り替わる。系譜が
        // purge 済みで再構築材料が無くてもリセットだけは行う (= 復元直後の
        // ライブと同じく「直前文書の絵は消える」)。
        resetForRestore(backgroundAt(options.events, ev.restoredFromSeq));
        await applySilently(rebuildEventsBySeq.get(ev.seq) ?? []);
        await breathe();
        continue;
      }
      if (ev.t === 'whiteboard.imagepaste' || ev.t === 'whiteboard.region') totalImagepastes++;
      if (
        (await applyEvent(core, undoStore, ev, options.logger, resolveImagepaste)) ===
        'imagepaste-skipped'
      ) {
        skippedImagepastes++;
      }
      await breathe();
    }

    // Full composite (bg + all layers + in-flight stroke buffers), then
    // crop (自動クロップ, identity when the rect is the full canvas) and
    // immediate shrink — the 64 MB-class original never outlives this
    // iteration (§5.1).
    const composed = core.compositeAllForExport();
    const cropped = cropRgba(composed, options.canvas.width, options.canvas.height, sourceRect);
    const scaled = scaler(
      cropped,
      sourceRect.width,
      sourceRect.height,
      output.width,
      output.height,
    );
    yield {
      index: k,
      timestampUs: k * durationUs,
      durationUs,
      width: output.width,
      height: output.height,
      // The scaler may return its input (identity path) whose buffer we'd
      // otherwise reuse conceptually; frames are consumed immediately by
      // T2 so no defensive copy is taken (documented on FrameScaler).
      rgba: scaled,
    };
  }

  return { appliedEvents: applied, totalImagepastes, skippedImagepastes };
}

// ─── Event application (mirror of SnapshotEngine.applyEvent, client side) ──

export const applyEvent = async (
  core: EngineCore,
  undoStore: UndoBaselineStore,
  ev: StrokeLogEvent,
  logger: Logger | undefined,
  resolveImagepaste: ResolveImagepasteFn | undefined,
): Promise<'applied' | 'imagepaste-skipped'> => {
  switch (ev.t) {
    case 'stroke.start':
      core.startStroke({
        sid: ev.sid,
        pen: ev.pen,
        color: ev.color,
        alpha: ev.alpha,
        width: ev.width,
        layerId: ev.layerId,
        mask: ev.mask ?? { type: 'none', colors: [] },
        ...(ev.penParams !== undefined ? { penParams: ev.penParams } : {}),
        aa: ev.aa,
        // Historical clock, not wall clock — keeps any watchdog semantics
        // deterministic (same convention as SnapshotEngine replay).
        nowMs: ev.ts,
      });
      // conflict-aware Undo (docs/_wip/undo-conflict-aware-2026-07-12.md f1):
      // the raw stroke log carries `stroke.cancelled` tombstones verbatim
      // ("clear や undo も過程として映る (味)" — §5.1, same philosophy). Mirror
      // every stroke into the SAME B' bookkeeping the live client uses so a
      // later `stroke.cancelled` in the log can bbox-locally rewind it.
      undoStore.onStrokeStart({
        sid: ev.sid,
        uid: ev.uid,
        layerId: ev.layerId,
        pen: ev.pen,
        color: ev.color,
        alpha: ev.alpha,
        width: ev.width,
        mask: ev.mask ?? { type: 'none', colors: [] },
        aa: ev.aa,
        ...(ev.penParams !== undefined ? { penParams: ev.penParams } : {}),
        startSeq: ev.seq,
      });
      return 'applied';
    case 'stroke.segment':
      // Unknown sid (start lost in a coverage gap / trimmed away) is a
      // silent no-op inside StrokeRenderer — the T0 gap-tolerance rule.
      core.addPoints(ev.sid, ev.pts, ev.ts);
      undoStore.onStrokeSegment(ev.sid, ev.pts);
      return 'applied';
    case 'stroke.end':
      core.endStroke(ev.sid);
      undoStore.onStrokeEnd(ev.sid, ev.seq);
      return 'applied';
    case 'stroke.cancelled': {
      // B' bbox-local rewind — see `UndoBaselineStore`. No `snapshot.published`
      // ever lands mid-timelapse-replay (this reads the raw log, not the live
      // WS), so the store's baseline stays empty and every rewind replays
      // straight from the tracked stroke list; that's fine, it's still
      // bbox-local (locality holds regardless of baseline non-emptiness).
      undoStore.onCancelled(ev.sid);
      for (const patch of undoStore.drainRewind()) {
        const w = patch.bbox.x1 - patch.bbox.x0 + 1;
        const h = patch.bbox.y1 - patch.bbox.y0 + 1;
        core.applyLayerRegion(patch.layerId, patch.bbox.x0, patch.bbox.y0, w, h, patch.rgba);
      }
      return 'applied';
    }
    case 'whiteboard.clear':
      // layerId present = single-layer clear, absent = full wipe — the
      // engine's clearLayer handles both. Clears are part of the process
      // video by design (§5.1 「clear や undo も過程として映る (味)」).
      core.clearLayer(ev.layerId);
      return 'applied';
    case 'whiteboard.background-set':
      // Alpha fixed opaque per the broadcast convention. The next frame's
      // compositeAllForExport picks the new bg up (export path composites
      // the bg, unlike the CSS-bg display path).
      core.setBackground({ rgb: ev.rgb, alpha: 255 });
      return 'applied';
    case 'whiteboard.imagepaste': {
      // vandalism-recovery-ui-2026-09-27.md「タイムラプスの imagepaste 再生
      // として先行実装する」節: `ev.url` is a presigned GET that expired
      // ~30 min after the original broadcast, so it's never fetchable
      // as-is — `resolveImagepaste` (when supplied) re-signs + re-fetches
      // it (`imagepasteResolve.ts`). No resolver at all = pre-existing MVP
      // behaviour (always skip; kept for callers/tests that don't care).
      //
      // `onImagepaste` (bbox metadata) is recorded regardless of whether
      // the resolver ultimately succeeds — mirrors the live client's split
      // timing (`CanvasDrawingEngine.ingestUndoBroadcast`'s comment) so a
      // later `stroke.cancelled` whose bbox overlaps this paste's footprint
      // rewinds correctly instead of silently ignoring the paste.
      if (resolveImagepaste === undefined) {
        logger?.warn('timelapseReplay: whiteboard.imagepaste skipped (no resolver)', {
          seq: ev.seq,
          layerId: ev.layerId,
        });
        return 'imagepaste-skipped';
      }
      undoStore.onImagepaste({
        layerId: ev.layerId,
        x: ev.x,
        y: ev.y,
        width: ev.width,
        height: ev.height,
        seq: ev.seq,
      });
      const decoded = await resolveImagepaste(ev);
      if (decoded === null) {
        undoStore.onImagepasteBitmap(ev.seq, null);
        logger?.warn('timelapseReplay: whiteboard.imagepaste failed to resolve, skipping', {
          seq: ev.seq,
          layerId: ev.layerId,
        });
        return 'imagepaste-skipped';
      }
      // Natural (decoded) size at the broadcast (x, y) — NEVER resized to
      // ev.width/height (module header; matches EngineCore.applyImagepaste's
      // own contract, which paints at the rgba buffer's own dimensions).
      core.applyImagepaste({
        layerId: ev.layerId,
        rgba: decoded.rgba,
        width: decoded.width,
        height: decoded.height,
        x: ev.x,
        y: ev.y,
      });
      undoStore.onImagepasteBitmap(ev.seq, decoded);
      return 'applied';
    }
    case 'whiteboard.region': {
      // 範囲の移動・コピー: 再署名した items[].url の PNG を純 JS で decode →
      // 共有の `applyRegionMove` (消す → 置く)。画像が 1 枚でも取れなければ全体を
      // 適用しない (ライブ・server と同じ。「スキップ」として数える = imagepaste と同じ扱い)。
      // メタは画像の成否に関わらず先に取り込む (後続の stroke.cancelled の窓のため)。
      const spec = { mode: ev.mode, src: ev.src, dst: ev.dst, items: ev.items };
      undoStore.onRegion({ ...spec, seq: ev.seq });
      const resolveRegion = resolveImagepaste?.region;
      const images = resolveRegion === undefined ? null : await resolveRegion(ev);
      undoStore.onRegionImages(ev.seq, images);
      if (images === null || !applyRegionMove(core, spec, images).applied) {
        logger?.warn('timelapseReplay: whiteboard.region failed to resolve, skipping', {
          seq: ev.seq,
        });
        return 'imagepaste-skipped';
      }
      return 'applied';
    }
    case 'whiteboard.region-cancelled': {
      // 範囲の移動・コピーの取り消し (tombstone)。線の cancel と同じく、その移動を除いて
      // 元と置き先の矩形 (全対象層) を作り直す。未知 seq / 画像が取れず適用しなかった移動の
      // 取り消しでも矛盾しない (作り直しは「その移動が無い絵」になるだけ)。
      undoStore.onRegionCancelled(ev.regionSeq);
      for (const patch of undoStore.drainRewind()) {
        const w = patch.bbox.x1 - patch.bbox.x0 + 1;
        const h = patch.bbox.y1 - patch.bbox.y0 + 1;
        core.applyLayerRegion(patch.layerId, patch.bbox.x0, patch.bbox.y0, w, h, patch.rgba);
      }
      return 'applied';
    }
    case 'whiteboard.restore':
      // 過去キャンバス復元の系譜マーカー (§4.5-1/§4.5-2)。系譜再構築
      // (リセット + 復元元範囲の無音再適用) は replayFrames のループ側 /
      // seed 計画 (`lineageSeed.ts`) が担う — ここに来るのは系譜ステップ内に
      // 迷い込んだ防御ケースのみで、単に消費する (系譜範囲は境界フリーに
      // 構築されるため通常は到達しない)。`snapshot` の presigned URL は
      // 期限切れ済みなので使わない (耐久 ID からの再適用で bit-perfect)。
      return 'applied';
  }
};

/**
 * Layer count for the offscreen engine: enough for every layerId the log
 * touches, floored at the Classic default so an empty / stroke-less log
 * still allocates the standard stack.
 */
export const deriveLayerCount = (events: readonly StrokeLogEvent[]): number => {
  let maxLayerId = -1;
  for (const ev of events) {
    if (ev.t === 'stroke.start' || ev.t === 'whiteboard.imagepaste') {
      if (ev.layerId > maxLayerId) maxLayerId = ev.layerId;
    } else if (ev.t === 'whiteboard.region') {
      for (const it of ev.items) {
        maxLayerId = Math.max(maxLayerId, it.srcLayerId, it.dstLayerId);
      }
    } else if (ev.t === 'whiteboard.clear' && ev.layerId !== undefined) {
      if (ev.layerId > maxLayerId) maxLayerId = ev.layerId;
    }
  }
  return Math.max(CLASSIC_LAYER_COUNT, maxLayerId + 1);
};
